import {
  Add,
  Attachment,
  ContentCopy,
  Delete,
  DragIndicator,
  Edit
} from '@mui/icons-material';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  styled
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNotification } from '../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../lib/userUtils';
import {
  addTestSteps,
  changeStepNumber,
  cloneTestSteps,
  deleteTestStep,
  editTestStep,
  getAllTestSteps,
} from '../../../modules/teststep';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionMenu from '../../../ui/primitives/ActionMenu';
import { CloneChip, CountChip } from '../../../ui/primitives/Chips';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import CustomInputField from '../../../ui/primitives/CustomInputField';
import TestStepAttachments from './TestStepAttachments';
import { AttachmentIcon } from '../../../ui/primitives/CustomIcons';
import { renderLinks } from '../../../lib/utils';

const COLUMNS = [
  { id: 'sNo', label: '#', width: '64px' },
  { id: 'step', label: 'Description', width: '36%' },
  { id: 'testData', label: 'Test Data', width: '22%' },
  { id: 'expectedResult', label: 'Expected Result', width: '22%' },
];
const URI_COLUMN = { id: 'uri', label: 'URI', width: '14%' };

const BASE_FIELDS = [
  { id: 'step', label: 'Description', placeholder: 'Enter step description', required: true, maxLength: 2000 },
  { id: 'testData', label: 'Test Data', placeholder: 'Enter test data', maxLength: 2000 },
  { id: 'expectedResult', label: 'Expected Result', placeholder: 'Enter expected result', maxLength: 2000 },
];
const URI_FIELD = { id: 'uri', label: 'URI', placeholder: 'https://api.example.com/endpoint', maxLength: 250 };

const EMPTY_SCRIPT = { step: '', testData: '', expectedResult: '', uri: '' };

// ─── Styled components ────────────────────────────────────────────────────────

const CellText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'muted' && prop !== 'expanded',
})(({ theme, muted, expanded }) => ({
  color: muted ? theme.palette.text.disabled : theme.palette.text.primary,
  fontStyle: muted ? 'italic' : 'normal',
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  display: expanded ? 'block' : '-webkit-box',
  WebkitLineClamp: expanded ? 'unset' : 3,
  WebkitBoxOrient: 'vertical',
  overflow: expanded ? 'visible' : 'hidden',
}));

CellText.defaultProps = { variant: 'body2' };

const ExpandableText = styled(Typography)(({ expanded }) => ({
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  display: expanded ? 'block' : '-webkit-box',
  WebkitLineClamp: expanded ? 'unset' : 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  padding: 0,
  minWidth: 'auto',
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'none',
  color: theme.palette.primary.main,
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: 'transparent',
  },
}));

const ExpandableCellText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const isOverflowing = el.scrollHeight > el.clientHeight;
    setShowToggle(isOverflowing);
  }, [text]);

  return (
    <Box>
      <ExpandableText
        ref={ref}
        expanded={expanded ? 1 : 0}
        variant="body2"
      >
        {text ? renderLinks(text) : '—'}
      </ExpandableText>

      {showToggle && (
        <ReadMoreButton onClick={() => setExpanded((p) => !p)}>
          {expanded ? 'Show less' : 'Read more'}
        </ReadMoreButton>
      )}
    </Box>
  );
};

const BodyCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.375),
  paddingBottom: theme.spacing(1.375),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  verticalAlign: 'top',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeadCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.grey[900], 0.025),
  borderBottom: `2px solid ${theme.palette.divider}`,
  '&:first-of-type': { borderLeft: 'none' },
}));

const AddFirstStepLink = styled(Box)(({ theme }) => ({
  display: 'inline',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  fontWeight: 600,
  '&:hover': { textDecoration: 'underline' },
}));

const LoadingRows = ({ count = 5, cols }) =>
  Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      {cols.map((col) => (
        <BodyCell key={col.id}>
          <Skeleton variant="text" width="70%" height={18} />
        </BodyCell>
      ))}
      <BodyCell />
    </TableRow>
  ));


const InlineStepForm = ({
  stepNo,
  fields,
  initialValues,
  onSave,
  onCancel,
  submitting,
  rowRef,
}) => {
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const isSaveDisabled = submitting || !values.step?.trim();

  return (
    <TableRow
      ref={rowRef}
      sx={{
        backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
        '& td': { verticalAlign: 'top' },
      }}
    >
      <BodyCell sx={{ width: '64px', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="flex-start" spacing={0.75}>
          <DragIndicator
            fontSize="small"
            sx={{ color: 'text.disabled', fontSize: '1rem' }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            {stepNo}
          </Typography>
        </Stack>
      </BodyCell>

      {fields.map((field, idx) => {
        const isLastField = idx === fields.length - 1;

        return (
          <BodyCell key={field.id}>
            <Stack spacing={1}>
              <CustomInputField
                id={field.id}
                value={values[field.id] ?? ''}
                onChange={(id, val) => handleChange(id, val)}
                placeholder={field.placeholder}
                required={field.required}
                maxLength={field.maxLength}
                multiline
                rows={3}
                autoFocus={idx === 0}
              />

              {isLastField && (
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onSave(values)}
                    disabled={isSaveDisabled}
                  >
                    {submitting ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              )}
            </Stack>
          </BodyCell>
        );
      })}
      <BodyCell sx={{ width: '52px' }} />
    </TableRow>
  );
};

const INITIAL_SCRIPT = { step: '', testData: '', expectedResult: '' };

const TestStepsContainer = ({ data, testCaseId }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ scripts: [] });
  const [totalScripts, setTotal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedScript, setSelected] = useState(INITIAL_SCRIPT);
  const [openAttachments, setOpenAtt] = useState(false);
  const [openConfirmation, setConfirm] = useState(false);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isReordering, setIsReordering] = useState(false);

  const tableContainerRef = useRef(null);
  const addRowRef = useRef(null);
  const { addNotification } = useNotification();

  const technology_layer = data?.technology_layer;
  const isViewer = isExecutiveManager();
  const isDragDisabled = isViewer || !!data?.is_archived || isReordering || isAdding || !!editingId;
  const columns = technology_layer === 'API/Integration' ? [...COLUMNS, URI_COLUMN] : COLUMNS;
  const fields = technology_layer === 'API/Integration' ? [...BASE_FIELDS, URI_FIELD] : BASE_FIELDS;

  const fetchScripts = async () => {
    try {
      const response = await getAllTestSteps(testCaseId);
      if (response?.rows && Array.isArray(response.rows)) {
        setTotal(response.rows.length);
        const sorted = response.rows
          .map((s) => ({
            testData: s.test_data,
            expectedResult: s.expected_result,
            step: s.step,
            attachments: s.attachments,
            id: s.id,
            uri: s.uri,
            is_cloned: s.is_cloned,
            sNo: s.s_no,
          }))
          .sort((a, b) => a.sNo - b.sNo);
        setFormData({ scripts: sorted });
      }
    } catch {
      addNotification('danger', 'Failed to fetch test steps.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    if (data?.id) fetchScripts();
  }, [data?.id]);

  useEffect(() => {
    if (isAdding && addRowRef.current) {
      setTimeout(() => {
        addRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [isAdding]);

  const handleAdd = () => {
    setEditingId(null);
    setIsAdding(true);
  };

  const handleAddSave = async (values) => {
    setSubmitting(true);
    try {
      await addTestSteps(
        {
          testSteps: [
            {
              id: null,
              step: values.step,
              testData: values.testData,
              expectedResult: values.expectedResult,
              uri: values.uri || '',
            },
          ],
        },
        testCaseId
      );
      addNotification('success', 'Test step added successfully!');
      setIsAdding(false);
      fetchScripts();
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to add test step. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCancel = () => setIsAdding(false);

  const handleEdit = (row) => {
    setIsAdding(false);
    setEditingId(row.id);
  };

  const handleEditSave = async (values, rowId) => {
    setSubmitting(true);
    try {
      const testStep = {
        id: rowId,
        step: values.step.trim(),
        testData: values.testData?.trim() ?? '',
        expectedResult: values.expectedResult?.trim() ?? '',
        uri: values.uri || '',
      };

      await editTestStep({ testStep }, rowId);

      addNotification('success', 'Test step updated successfully!');
      setEditingId(null);
      fetchScripts();
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to update test step.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCancel = () => setEditingId(null);

  const handleAttachment = (row) => { setSelected(row); setOpenAtt(true); };
  const handleOpenConfirm = (row) => { setSelected(row); setConfirm(true); };
  const handleConfirmClose = () => { setSelected(null); setConfirm(false); };

  const handleClone = async (row) => {
    const { created_at, updated_at, attachments, ...rest } = row;
    try {
      await cloneTestSteps({ testStep: { ...rest, isCloning: row?.id } }, testCaseId);
      addNotification('success', 'Test step cloned successfully!');
      fetchScripts();
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to clone test step.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTestStep(selectedScript?.id);
      addNotification('success', 'Test step deleted successfully!');
      handleConfirmClose();
      fetchScripts();
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to delete test step.');
    }
  };

  const getMenuItems = (row) => [
    {
      label: 'Edit',
      icon: <Edit />,
      onClick: () => handleEdit(row),
      hidden: isViewer,
    },
    {
      label: 'Clone',
      icon: <ContentCopy />,
      onClick: () => handleClone(row),
      hidden: isViewer,
    },
    {
      label: row?.attachments?.length > 0 ? 'View Attachments' : 'Add Attachment',
      icon: <Attachment />,
      onClick: () => handleAttachment(row),
    },
    {
      label: 'Delete',
      icon: <Delete />,
      onClick: () => handleOpenConfirm(row),
      color: 'error',
      hidden: isViewer,
    },
  ];

  const handleDragStart = (e, index) => {
    if (isDragDisabled || loading) { e.preventDefault(); return; }
    setOriginalOrder([...formData.scripts]);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.style.cssText =
      'background:#f5f5f5;padding:6px 12px;border-radius:4px;font-size:0.85rem;font-weight:600;';
    ghost.textContent = `Step ${formData.scripts[index].sNo}`;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragLeave = (e) => {
    if (e.target === tableContainerRef.current) setDragOverIndex(null);
  };

  const handleDragEnd = () => { setDraggedIndex(null); setDragOverIndex(null); };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null); setDragOverIndex(null); return;
    }
    const updated = [...formData.scripts];
    const [dragged] = updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, dragged);
    const reordered = updated.map((s, idx) => ({ ...s, sNo: idx + 1 }));
    setFormData({ scripts: reordered });
    setDraggedIndex(null); setDragOverIndex(null);
    setIsReordering(true);
    try {
      await changeStepNumber(reordered);
      addNotification('success', 'Test steps reordered successfully!');
      fetchScripts();
    } catch (err) {
      setFormData({ scripts: originalOrder });
      addNotification('danger', err?.message || 'Failed to reorder test steps.');
    } finally {
      setIsReordering(false);
    }
  };

  const cardTitle = (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography variant="h6">Test Steps</Typography>
      {!loading && <CountChip label={totalScripts} />}
    </Stack>
  );

  const cardActions = !isViewer && (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={handleAdd}
      disabled={data?.is_archived || isAdding}
    >
      Add Step
    </Button>
  );

  let scrollFrame;

  const handleContainerDragOver = (e) => {
    if (!tableContainerRef.current) return;

    const container = tableContainerRef.current;
    const rect = container.getBoundingClientRect();

    const THRESHOLD = 60;
    const MAX_SPEED = 20;

    const offsetY = e.clientY - rect.top;

    let scrollSpeed = 0;

    if (offsetY < THRESHOLD) {
      scrollSpeed = -MAX_SPEED * ((THRESHOLD - offsetY) / THRESHOLD);
    } else if (offsetY > rect.height - THRESHOLD) {
      scrollSpeed = MAX_SPEED * ((offsetY - (rect.height - THRESHOLD)) / THRESHOLD);
    }

    cancelAnimationFrame(scrollFrame);

    scrollFrame = requestAnimationFrame(() => {
      container.scrollTop += scrollSpeed;
    });
  };

  return (
    <>
      <DetailsCard title={cardTitle} actions={cardActions}>
        <TableContainer
          ref={tableContainerRef}
          onDragOver={handleContainerDragOver}
          className="test-scripts-table-container"
          sx={{ maxHeight: 520, overflowY: 'auto' }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 640, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <HeadCell key={col.id} sx={{ width: col.width }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      {col.label}
                    </Typography>
                  </HeadCell>
                ))}
                <HeadCell sx={{ width: '52px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <LoadingRows count={4} cols={columns} />

              ) : formData.scripts.length === 0 && !isAdding ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ border: 'none' }}>
                    <Box sx={{ py: 7, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No test steps yet.{' '}
                        {!isViewer && !data?.is_archived && (
                          <AddFirstStepLink component="span" onClick={handleAdd}>
                            Add the first step.
                          </AddFirstStepLink>
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>

              ) : (
                <>
                  {formData.scripts.map((row, rowIndex) => {
                    const isDragging = draggedIndex === rowIndex;
                    const isDragOver = dragOverIndex === rowIndex;
                    const isEditing = editingId === row.id;

                    return (
                      <React.Fragment key={row.id}>
                        {isEditing ? (
                          <InlineStepForm
                            stepNo={row.sNo}
                            fields={fields}
                            initialValues={{
                              step: row.step ?? '',
                              testData: row.testData ?? '',
                              expectedResult: row.expectedResult ?? '',
                              uri: row.uri ?? '',
                            }}
                            onSave={(values) => handleEditSave(values, row.id)}
                            onCancel={handleEditCancel}
                            submitting={submitting}
                          />
                        ) : (
                          <TableRow
                            draggable={!isDragDisabled}
                            hover
                            onDragStart={(e) => handleDragStart(e, rowIndex)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverIndex(rowIndex);
                            }}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, rowIndex)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              cursor: !isDragDisabled ? 'grab' : 'default',
                              opacity: isDragging ? 0.5 : 1,
                              backgroundColor: isDragOver
                                ? (t) => alpha(t.palette.primary.main, 0.06)
                                : 'inherit',
                            }}
                          >
                            <BodyCell sx={{ width: '70px' }}>
                              <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                                <DragIndicator
                                  fontSize="small"
                                  sx={{ color: 'text.secondary', opacity: 0.7, fontSize: '1rem' }}
                                />

                                <Stack spacing={0.25}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.75rem',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    {row.sNo}
                                  </Typography>

                                  {row.is_cloned && (
                                    <CloneChip label="clone" size="small" />
                                  )}
                                </Stack>
                              </Stack>
                            </BodyCell>

                            <BodyCell>
                              <ExpandableCellText text={row.step} />
                            </BodyCell>

                            <BodyCell>
                              <ExpandableCellText text={row.testData} />
                            </BodyCell>

                            <BodyCell>
                              <ExpandableCellText text={row.expectedResult} />
                            </BodyCell>

                            {technology_layer === 'API/Integration' && (
                              <BodyCell>
                                <ExpandableCellText text={row.uri} sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }} />
                              </BodyCell>
                            )}

                            <BodyCell align="right" sx={{ width: '80px', px: 1 }}>
                              <Stack direction="row" justifyContent="flex-end" spacing={1}>

                                {row?.attachments?.length > 0 && (
                                  <Tooltip title={row?.attachments?.length ? 'View Attachments' : 'Add Attachment'}>
                                    <AttachmentIcon onClick={() => handleAttachment(row)} />
                                  </Tooltip>
                                )}

                                <ActionMenu items={getMenuItems(row)} />
                              </Stack>
                            </BodyCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {isAdding && (
                    <InlineStepForm
                      rowRef={addRowRef}
                      stepNo={totalScripts + 1}
                      fields={fields}
                      initialValues={EMPTY_SCRIPT}
                      onSave={handleAddSave}
                      onCancel={handleAddCancel}
                      submitting={submitting}
                    />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DetailsCard>

      <TestStepAttachments
        show={openAttachments}
        handleClose={() => setOpenAtt(false)}
        testStepId={selectedScript?.id}
        selectedScript={selectedScript}
        fetchScripts={fetchScripts}
      />

      <ConfirmationModal
        show={openConfirmation}
        handleConfirm={handleDelete}
        handleClose={handleConfirmClose}
        title="Remove Test Step"
        body={
          <Typography variant="body2">
            Are you sure you want to remove{' '}
            <strong>step {selectedScript?.sNo}</strong>? This will also remove
            any attachments or execution results associated with it.
          </Typography>
        }
        buttonLabel="Yes, Remove"
        buttonLabelSecondary="Cancel"
      />
    </>
  );
};

export default TestStepsContainer;